import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  FileArchive,
  FolderOpen,
  Loader2,
  Pencil,
  X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import type { JobOut } from "@/lib/api/jobs";
import { downloadRequestPdfZip } from "@/lib/api/requests";
import { cn } from "@/lib/cn";

import StatusBadge from "../jobs/StatusBadge";
import { formatDateTime, progressPercent } from "../jobs/format";
import RequestCsvSection from "./RequestCsvSection";
import { useRequestDetail, useUpdateRequest } from "./useRequests";

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function RequestDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: req, isLoading, isError, error } = useRequestDetail(id);
  const updateMutation = useUpdateRequest();
  const pdfZip = useMutation({
    mutationFn: async () => {
      if (!req) return;
      const { blob, filename } = await downloadRequestPdfZip(req.id);
      triggerBrowserDownload(blob, filename);
    },
  });
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");

  if (isLoading) {
    return <p className="text-sm text-slate-400">{t("common.loading")}</p>;
  }
  if (isError || !req) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>
          {error instanceof ApiError ? error.detail : ((error as Error)?.message ?? "not found")}
        </span>
      </div>
    );
  }

  const pct = progressPercent(
    req.file_count,
    req.success_count,
    req.rejected_count,
    req.error_count,
  );

  const startEdit = () => {
    setTitle(req.title ?? "");
    setEditing(true);
  };
  const save = () => {
    const next = title.trim();
    updateMutation.mutate(
      { requestId: req.id, title: next.length > 0 ? next : null },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <Link
        to="/requests"
        className="inline-flex items-center gap-1 text-xs text-slate-500 transition hover:text-slate-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {t("requests.detail.back")}
      </Link>

      <header className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      save();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setEditing(false);
                    }
                  }}
                  maxLength={255}
                  disabled={updateMutation.isPending}
                  placeholder={t("requests.list.renamePlaceholder")}
                  className="w-full max-w-md rounded-md border border-blue-400 px-2 py-1 text-lg font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
                <button
                  type="button"
                  onClick={save}
                  disabled={updateMutation.isPending}
                  className="rounded p-1.5 text-emerald-600 transition hover:bg-emerald-50 disabled:opacity-50"
                  aria-label={t("common.actions.save")}
                >
                  <Check className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  disabled={updateMutation.isPending}
                  className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 disabled:opacity-50"
                  aria-label={t("common.actions.cancel")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="truncate text-xl font-semibold text-slate-900">
                  {req.title || (
                    <span className="text-slate-400">{t("common.placeholders.anonymous")}</span>
                  )}
                </h1>
                <button
                  type="button"
                  onClick={startEdit}
                  className="rounded p-1 text-slate-300 transition hover:bg-blue-50 hover:text-blue-600"
                  aria-label={t("requests.list.renameTitle")}
                  title={t("requests.list.renameTitle")}
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            )}
            {req.source_name && (
              <p className="mt-1 truncate text-xs text-slate-400">{req.source_name}</p>
            )}
            <p className="mt-1 text-xs text-slate-400" title={formatDateTime(req.created_at)}>
              {formatDateTime(req.created_at)}
            </p>
          </div>
          <StatusBadge status={req.status} />
        </div>

        {/* 집계 지표 */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t("requests.detail.stats.jobs")} value={req.job_count} />
          <Stat label={t("requests.detail.stats.files")} value={req.file_count} />
          <Stat
            label={t("requests.detail.stats.reviewed")}
            value={`${req.reviewed_count}/${req.file_count}`}
          />
          <Stat
            label={t("requests.detail.stats.result")}
            value={`✓${req.success_count} ⚠${req.rejected_count} ✗${req.error_count}`}
          />
        </div>

        {req.status !== "completed" && (
          <div className="mt-3">
            <div className="mb-1 flex justify-between text-[11px] text-slate-400">
              <span>
                {t("requests.detail.progress", { done: req.done_jobs, total: req.job_count })}
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full bg-blue-500 transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </header>

      {/* 원본 PDF 일괄 다운로드 — 처리 결과와 무관하게 파일이 있으면 노출 */}
      {req.file_count > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                {t("requests.detail.pdfZip.title")}
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {t("requests.detail.pdfZip.description")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => pdfZip.mutate()}
              disabled={pdfZip.isPending}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50",
                pdfZip.isPending && "cursor-wait",
              )}
            >
              {pdfZip.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileArchive className="h-3.5 w-3.5" />
              )}
              {t("requests.detail.pdfZip.button")}
            </button>
          </div>
          {pdfZip.isError && (
            <p
              role="alert"
              className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {t("jobs.detail.pdfZip.error")}:{" "}
              {pdfZip.error instanceof ApiError ? pdfZip.error.detail : pdfZip.error?.message}
            </p>
          )}
        </section>
      )}

      {(req.success_count > 0 || req.rejected_count > 0) && (
        <RequestCsvSection
          requestId={req.id}
          successCount={req.success_count}
          rejectedCount={req.rejected_count}
        />
      )}

      {/* 하위 작업 목록 */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t("requests.detail.jobsTitle", { count: req.jobs.length })}
        </h2>
        <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {req.jobs.map((job, idx) => (
            <JobRow key={job.id} job={job} border={idx > 0} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <p className="text-[11px] text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}

function JobRow({ job, border }: { job: JobOut; border: boolean }) {
  const { t } = useTranslation();
  const pct = progressPercent(
    job.file_count,
    job.success_count,
    job.rejected_count,
    job.error_count,
  );
  return (
    <li
      className={cn(
        "group relative transition hover:bg-slate-50",
        border && "border-t border-slate-100",
      )}
    >
      <Link
        to={`/jobs/${job.id}`}
        className="absolute inset-0 z-0"
        aria-label={job.batch_name || job.id}
      />
      <div className="pointer-events-none relative z-10 grid grid-cols-12 items-center gap-3 px-4 py-3 text-sm">
        <div className="col-span-5 min-w-0">
          <div className="flex items-center gap-1.5">
            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <p className="truncate font-medium text-slate-900">
              {job.source_folder || job.batch_name || t("requests.detail.rootFolder")}
            </p>
          </div>
        </div>
        <div className="col-span-3 flex items-center gap-2">
          <StatusBadge status={job.status} />
          <span className="text-xs text-slate-500">
            {t("jobs.list.fileCount", { count: job.file_count })}
          </span>
        </div>
        <div className="col-span-4">
          {job.status === "completed" || job.status === "failed" ? (
            <div className="flex items-center gap-1.5 text-[11px]">
              {job.success_count > 0 && (
                <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">
                  ✓ {job.success_count}
                </span>
              )}
              {job.rejected_count > 0 && (
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                  ⚠ {job.rejected_count}
                </span>
              )}
              {job.error_count > 0 && (
                <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">
                  ✗ {job.error_count}
                </span>
              )}
            </div>
          ) : (
            <div className="h-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full bg-blue-500 transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
